import {
  TAllMasterdataListsByBctx,
  TBctxItemWithoutSize,
  TBCTXKeysWithoutSize,
  TCombination,
  TMasterdataListByProductTypeAndStyle,
  TMasterdataListDB,
  TOperationDTOWithMappedIssuesAndFilteredIssuesListDTO,
  TOperationDTOWithMappedIssuesDTO,
  TOperationIssueResponseDTO,
  TOperationsAndIssuesAndSilhouetteOperations,
  TREQUEST_masterdataLists,
  TREQUEST_silhouetteKeys,
  TRESPONSE_masterdataLists,
  TRESPONSE_masterdataListsV2,
  TRESPONSE_silhouetteKeys,
} from '@monorepo/types';
import produce from 'immer';
import * as _ from 'lodash';
import moment from 'moment';
import { BCTXComputationHelper } from './bctx-computation-helper';

export const MasterdataUtil = {
  getFrontAndBackImageIds(silhouetteName: string) {
    return {
      frontImageId: silhouetteName + '_Front.svg',
      backImageId: silhouetteName + '_Back.svg',
    };
  },

  getAllMasterdataListsPayload(
    bctxKeysOrderWithoutSize: TBCTXKeysWithoutSize[],
    bctxLists: TBctxItemWithoutSize[][],
    listKeys: string[],
    subjectKey: string,
    listConf: string[],
  ): TREQUEST_masterdataLists {
    const combinations: TCombination[] = bctxLists.map((bctx) => {
      const filteredBctx = MasterdataUtil.getFilteredBctx(bctx, listConf);
      const req_id = BCTXComputationHelper.getUniqueBctxKeyWithOrder(bctxKeysOrderWithoutSize, filteredBctx);
      return {
        req_id: req_id,
        tags: Object.fromEntries(filteredBctx.map((x) => [x.k, x.v])),
        list_names: listKeys,
        subject_key: subjectKey,
      };
    });
    combinations.push({
      list_names: listKeys,
      req_id: 'default',
      subject_key: subjectKey,
      tags: { default: 'default' },
    });
    return {
      combinations: _.uniqBy(combinations, 'req_id'),
      query_name: 'get_all_lists',
      subject_key: subjectKey,
    };
  },

  getFilteredBctx(bctx: TBctxItemWithoutSize[], config: string[]) {
    return bctx.filter(({ k, v }) => (config.length === 0 ? true : config.some((config) => config === k)));
  },
  getSilhouetteKeys(
    bctxKeysOrderWithoutSize: TBCTXKeysWithoutSize[],
    bctxLists: TBctxItemWithoutSize[][],
    listConf: string[],
  ) {
    const set = new Set<string>();
    return bctxLists.reduce((acc, bctx) => {
      const filteredBctx = MasterdataUtil.getFilteredBctx(bctx, listConf);
      const productTypeOrProductCategory = BCTXComputationHelper.getProductTypeOrProductCategoryFromBctxItems(bctx);
      const style = BCTXComputationHelper.getValueByKeyInBctxItems(bctx, 'style');
      if (productTypeOrProductCategory && style) {
        const id = productTypeOrProductCategory + '_' + style;
        if (!set.has(id)) {
          set.add(id);
          acc.push({
            key: BCTXComputationHelper.getUniqueBctxKeyWithOrder(bctxKeysOrderWithoutSize, filteredBctx),
            productType: productTypeOrProductCategory,
            style,
          });
        }
      }
      return acc;
    }, [] as { key: string; productType: string; style: string }[]);
  },
  getSilhouetteKeysPayload(
    bctxKeysOrderWithoutSize: TBCTXKeysWithoutSize[],
    bctxLists: TBctxItemWithoutSize[][],
    subjectKey: string,
    listConf: string[],
  ): TREQUEST_silhouetteKeys {
    const combinations: TCombination[] = bctxLists.map((bctx) => {
      const filteredBctx = MasterdataUtil.getFilteredBctx(bctx, listConf);
      return {
        req_id: BCTXComputationHelper.getUniqueBctxKeyWithOrder(bctxKeysOrderWithoutSize, filteredBctx),
        tags: Object.fromEntries(filteredBctx.map((x) => [x.k, x.v])),
        list_names: ['silhouette'],
        subject_key: subjectKey,
      };
    });
    return {
      combinations: _.uniqBy(combinations, 'req_id'),
      query_name: 'get_all_lists',
      subject_key: subjectKey,
    };
  },

  getAllOperationIssueDepartments(
    operationsAndIssuesDepartmentId: string,
    operationsAndIssuesAllDepartmentIds: string[] | undefined,
  ) {
    const allOperationIssueDepartments = operationsAndIssuesAllDepartmentIds
      ? _.uniq([operationsAndIssuesDepartmentId, ...operationsAndIssuesAllDepartmentIds])
      : [operationsAndIssuesDepartmentId];

    return allOperationIssueDepartments;
  },

  getFormattedDate() {
    return moment().format('DD-MM-YYYY');
  },

  getUpdatedDBWithAllMasterdataLists(currentDB: TMasterdataListDB, response: TRESPONSE_masterdataLists) {
    const flushedAllMasterdataListsByBctx = MasterdataUtil.getFlushedAllMasterdataListsByBctx(
      currentDB.allMasterdataListsByBctx,
      response.response.map(({ req_id }) => req_id),
    );
    currentDB = produce(currentDB, (draft) => {
      draft.allMasterdataListsByBctx = flushedAllMasterdataListsByBctx;
    });

    const currentTime = new Date().getTime();
    return produce(currentDB, (draft) => {
      response.response.forEach((allListsByBctx) => {
        draft.allMasterdataListsByBctx[allListsByBctx.req_id] = Object.keys(allListsByBctx.data).reduce((acc, curr) => {
          if (allListsByBctx.data[curr].list && allListsByBctx.data[curr].list.length) {
            acc[curr] = {
              lastSyncedAt: currentTime,
              masterdataList: allListsByBctx.data[curr],
              filteredMasterdataList:
                !draft.allMasterdataListsByBctx[allListsByBctx.req_id] ||
                !draft.allMasterdataListsByBctx[allListsByBctx.req_id][curr]
                  ? []
                  : draft.allMasterdataListsByBctx[allListsByBctx.req_id][curr].filteredMasterdataList.filter((id) => {
                      return allListsByBctx.data[curr].list.find(({ name }) => id === name);
                    }),
            };
          }
          return acc;
        }, {} as TMasterdataListDB['allMasterdataListsByBctx'][string]);
      });
    });
  },

  getUpdatedDBWithAllMasterdataListsV2(currentDB: TMasterdataListDB, response: TRESPONSE_masterdataListsV2) {
    const currentTime = new Date().getTime();
    //only for the response departments, if product-type and style combination is not present we flush the data(if expires)
    //otherwise we are updating the lastSyncedAt
    const flushedAllMasterdataListsV2 = MasterdataUtil.getFlushedAllMasterdataListsByBctxV2(
      currentDB.masterdataListV2,
      response.departmentWiseOperationsAndIssues,
      currentTime,
    );
    currentDB = produce(currentDB, (draft) => {
      draft.masterdataListV2 = flushedAllMasterdataListsV2;
    });

    Object.keys(response.departmentWiseOperationsAndIssues).forEach((department) => {
      const genericIssues = response.departmentWiseOperationsAndIssues[department].genericIssues;
      currentDB = produce(currentDB, (draft) => {
        if (draft.masterdataListV2[department]) {
          draft.masterdataListV2[department].genericIssues.list = genericIssues;
          draft.masterdataListV2[department].genericIssues.filteredDefectList = draft.masterdataListV2[
            department
          ].genericIssues.filteredDefectList.filter((defectName) => {
            return !!genericIssues.find((genericIssue) => genericIssue.issueName === defectName);
          });
          draft.masterdataListV2[department].genericIssues.filteredRejectList = draft.masterdataListV2[
            department
          ].genericIssues.filteredRejectList.filter((defectName) => {
            return !!genericIssues.find((genericIssue) => genericIssue.issueName === defectName);
          });
          response.departmentWiseOperationsAndIssues[department].operationsAndIssues.forEach(
            ({ productType, style, operations }) => {
              if (!draft.masterdataListV2[department].listByProductTypeAndStyle[productType]) {
                draft.masterdataListV2[department].listByProductTypeAndStyle[productType] = {
                  [style]: this.getInitOpertionAndIssueAndSilhouetteOperationsData(currentTime, operations),
                };
              } else if (!draft.masterdataListV2[department].listByProductTypeAndStyle[productType][style]) {
                draft.masterdataListV2[department].listByProductTypeAndStyle[productType][style] =
                  this.getInitOpertionAndIssueAndSilhouetteOperationsData(currentTime, operations);
              } else {
                const currentOperations =
                  draft.masterdataListV2[department].listByProductTypeAndStyle[productType][style].operations;
                draft.masterdataListV2[department].listByProductTypeAndStyle[productType][
                  style
                ].operations.filteredOperationsList = draft.masterdataListV2[department].listByProductTypeAndStyle[
                  productType
                ][style].operations.filteredOperationsList.filter((operation) => {
                  return !!operations.find((responseOpertion) => responseOpertion.name === operation);
                });
                let updatedOperationsData: TOperationDTOWithMappedIssuesAndFilteredIssuesListDTO[] = [];
                operations.forEach((operationData) => {
                  const index = currentOperations.list.findIndex((operation) => operation.id === operationData.id);
                  updatedOperationsData = produce(updatedOperationsData, (draft) => {
                    draft.push({
                      ...operationData,
                      filteredIssuesList:
                        index > -1
                          ? currentOperations.list[index].filteredIssuesList.filter((issue) => {
                              return !!operationData.issues.find(
                                (operationIssue) => operationIssue.issueName === issue,
                              );
                            })
                          : [],
                    });
                  });
                });
                draft.masterdataListV2[department].listByProductTypeAndStyle[productType][style].operations.list =
                  updatedOperationsData;
              }
            },
          );
        } else {
          draft.masterdataListV2[department] = {
            genericIssues: {
              filteredDefectList: [],
              filteredRejectList: [],
              list: genericIssues,
            },
            listByProductTypeAndStyle: this.getResponseOperationAndIssueMappedToDb(
              response.departmentWiseOperationsAndIssues[department].operationsAndIssues,
              currentTime,
            ),
          };
        }
      });
    });
    return currentDB;
  },

  getResponseOperationAndIssueMappedToDb(
    operationsAndIssues: TOperationIssueResponseDTO[],
    currentTime: number,
  ): TMasterdataListByProductTypeAndStyle {
    const responseMappingToOperation = operationsAndIssues.reduce((acc, curr) => {
      if (!acc[curr.productType]) {
        acc[curr.productType] = {};
      }
      if (!acc[curr.productType][curr.style]) {
        acc[curr.productType][curr.style] = this.getInitOpertionAndIssueAndSilhouetteOperationsData(
          currentTime,
          curr.operations,
        );
      }
      return acc;
    }, {} as TMasterdataListByProductTypeAndStyle);

    return responseMappingToOperation;
  },

  getInitOpertionAndIssueAndSilhouetteOperationsData(
    currentTime: number,
    operations: TOperationDTOWithMappedIssuesDTO[],
  ): TOperationsAndIssuesAndSilhouetteOperations {
    return {
      operations: {
        lastSyncedAt: currentTime,
        list: operations.map((operation) => {
          return {
            ...operation,
            filteredIssuesList: [],
          };
        }),
        filteredOperationsList: [],
      },
      silhouetteOperations: {
        backImageOperations: [],
        frontImageOperations: [],
      },
    };
  },

  getUpdatedDBWithMasterdataListRefetching(
    currentDB: TMasterdataListDB,
    response: TRESPONSE_masterdataLists,
    masterdatKey: string,
  ) {
    const flushedAllMasterdataListsByBctx = MasterdataUtil.getFlushedAllMasterdataListsByBctx(
      currentDB.allMasterdataListsByBctx,
      response.response.map(({ req_id }) => req_id),
    );
    currentDB = produce(currentDB, (draft) => {
      draft.allMasterdataListsByBctx = flushedAllMasterdataListsByBctx;
    });

    const currentTime = new Date().getTime();
    return produce(currentDB, (draft) => {
      response.response.map((item) => {
        const { req_id, data } = item;
        if (!draft.allMasterdataListsByBctx[req_id]) {
          if (data[masterdatKey] && data[masterdatKey].list && data[masterdatKey].list.length) {
            draft.allMasterdataListsByBctx[req_id] = {
              [masterdatKey]: {
                masterdataList: data[masterdatKey],
                filteredMasterdataList: [],
                lastSyncedAt: currentTime,
              },
            };
          }
        } else if (!draft.allMasterdataListsByBctx[req_id][masterdatKey]) {
          if (data[masterdatKey] && data[masterdatKey].list && data[masterdatKey].list.length) {
            draft.allMasterdataListsByBctx[req_id][masterdatKey] = {
              masterdataList: data[masterdatKey],
              filteredMasterdataList: [],
              lastSyncedAt: currentTime,
            };
          }
        } else if (data[masterdatKey] && data[masterdatKey].list && data[masterdatKey].list.length) {
          draft.allMasterdataListsByBctx[req_id][masterdatKey] = {
            masterdataList: data[masterdatKey],
            filteredMasterdataList: draft.allMasterdataListsByBctx[req_id][masterdatKey].filteredMasterdataList.filter(
              (id) => {
                return data[masterdatKey].list.find(({ name }) => name === id);
              },
            ),
            lastSyncedAt: currentTime,
          };
        } else {
          delete draft.allMasterdataListsByBctx[req_id][masterdatKey];
        }
      });
    });
  },

  getUpdatedDBWithSilhouetteKeys(res: TMasterdataListDB, response: TRESPONSE_silhouetteKeys) {
    return produce(res, (draft) => {
      draft.silhouetteKeysInfo = {
        keys: response.response.reduce((acc, curr) => {
          if (curr.data && curr.data.silhouette && curr.data.silhouette.dis_name)
            acc[curr.req_id] = curr.data.silhouette.dis_name;
          return acc;
        }, {} as TMasterdataListDB['silhouetteKeysInfo']['keys']),
      };
    });
  },

  getDownloadSilhoutteQueryKey(subject_key: string, silhoutteNames: string[]) {
    const updatedSilhoutteNames = (silhoutteNames = silhoutteNames.map((name) => {
      return subject_key + '<<->>' + name;
    }));
    const downloadSilhoutteQueryKey = updatedSilhoutteNames.join('<<_>>');
    return downloadSilhoutteQueryKey;
  },

  getOperationAndIssueIdAndDepartment(operation: string, issue: string, department: string) {
    return operation + '_' + issue + '_' + department;
  },

  getAllDepartmentsList(currentDepartment: string, otherDepartments?: string[]) {
    return [currentDepartment, ...(otherDepartments || [])];
  },

  getSilhoutteImageUrlForWeb(url: string, subject_key: string, silhoutteFileName: string) {
    const fullUrl = url + '?subject_key=' + subject_key + '&siluhoutteName=' + silhoutteFileName;
    return fullUrl;
  },

  getFlushedAllMasterdataListsByBctx(allMasterdataListsByBctx: TAllMasterdataListsByBctx, activeKeys: string[]) {
    const currentTime = new Date().getTime();
    const activeBctxKeySet = new Set<string>();
    activeKeys.forEach((key) => activeBctxKeySet.add(key));

    let updatedAllMasterdataListsByBctx: TAllMasterdataListsByBctx = {};

    Object.keys(allMasterdataListsByBctx).forEach((bctxKey) => {
      if (!activeBctxKeySet.has(bctxKey)) {
        Object.keys(allMasterdataListsByBctx[bctxKey]).forEach((masterdataListKey) => {
          const masterdataDTO = allMasterdataListsByBctx[bctxKey][masterdataListKey];
          if (masterdataDTO.filteredMasterdataList.length && currentTime - masterdataDTO.lastSyncedAt < 86400000 * 3) {
            updatedAllMasterdataListsByBctx = produce(updatedAllMasterdataListsByBctx, (draft) => {
              if (!draft[bctxKey]) {
                draft[bctxKey] = {
                  [masterdataListKey]: masterdataDTO,
                };
              } else if (!draft[bctxKey][masterdataListKey]) {
                draft[bctxKey][masterdataListKey] = masterdataDTO;
              }
            });
          }
        });
      } else {
        updatedAllMasterdataListsByBctx = produce(updatedAllMasterdataListsByBctx, (draft) => {
          draft[bctxKey] = allMasterdataListsByBctx[bctxKey];
        });
      }
    });
    return updatedAllMasterdataListsByBctx;
  },
  getFlushedAllMasterdataListsByBctxV2(
    masterDataListV2: TMasterdataListDB['masterdataListV2'],
    departmentWiseOperationsAndIssues: TRESPONSE_masterdataListsV2['departmentWiseOperationsAndIssues'],
    currentTime: number,
  ) {
    const timeGapBetweenSync = 86400000 * 3;

    let updatedMasterDataListV2: TMasterdataListDB['masterdataListV2'] = {};
    Object.keys(departmentWiseOperationsAndIssues).forEach((department) => {
      if (masterDataListV2[department]) {
        const activeBctxKeyDeptSet = new Set<string>();

        departmentWiseOperationsAndIssues[department].operationsAndIssues.map((keys) => {
          activeBctxKeyDeptSet.add(`${keys.productType}_${keys.style}`);
        });

        updatedMasterDataListV2 = produce(updatedMasterDataListV2, (draft) => {
          draft[department] = {
            genericIssues: masterDataListV2[department].genericIssues,
            listByProductTypeAndStyle: {},
          };
        });
        Object.keys(masterDataListV2[department].listByProductTypeAndStyle).forEach((productType) => {
          Object.keys(masterDataListV2[department].listByProductTypeAndStyle[productType]).forEach((style) => {
            // Checking if productType_Style is not part of API response keeping data in offline storage till 3 days from lastSyncTime
            if (!activeBctxKeyDeptSet.has(`${productType}_${style}`)) {
              if (
                currentTime -
                  masterDataListV2[department].listByProductTypeAndStyle[productType][style].operations.lastSyncedAt <
                timeGapBetweenSync * 3
              ) {
                updatedMasterDataListV2 = produce(updatedMasterDataListV2, (draft) => {
                  if (!draft[department].listByProductTypeAndStyle[productType]) {
                    draft[department].listByProductTypeAndStyle[productType] = {
                      [style]: masterDataListV2[department].listByProductTypeAndStyle[productType][style],
                    };
                  } else {
                    draft[department].listByProductTypeAndStyle[productType][style] =
                      masterDataListV2[department].listByProductTypeAndStyle[productType][style];
                  }
                });
              }
            } else {
              updatedMasterDataListV2 = produce(updatedMasterDataListV2, (draft) => {
                if (!draft[department].listByProductTypeAndStyle[productType])
                  draft[department].listByProductTypeAndStyle[productType] = {};
              });

              updatedMasterDataListV2 = produce(updatedMasterDataListV2, (draft) => {
                draft[department].listByProductTypeAndStyle[productType][style] = {
                  ...masterDataListV2[department].listByProductTypeAndStyle[productType][style],
                  operations: {
                    ...masterDataListV2[department].listByProductTypeAndStyle[productType][style].operations,
                    lastSyncedAt: currentTime,
                  },
                };
              });
            }
          });
        });
      }
    });
    return updatedMasterDataListV2;
  },
};
